<template>
  <div class="chart-line">
    <div class="line-header">
      <div class="line-title">
        <!-- {{childQuery}} -->
        <BaseIcon
          :name="item.header.titleIcon"
          width="1em"
          height="1em"
          v-if="item.header.titleIcon"
          class="icon-margin"
        />
        {{ item.header.title }}
        <BaseIcon
          :name="item.header.subIconTitle"
          v-if="item.header.subIconTitle"
          width="1rem"
          height="1rem"
          class="icon-margin-suffix"
        />
      </div>
      <div
        class="container"
        v-if="item.showMetricDropdown || item.showmetricRate"
      >
        <div v-if="item.showMetricDropdown">
          <RexSelectV3
            :options="formattedMetricNames"
            :value="currentMetric"
            @onChange="onMetricChange"
            :objectValueIndex="1"
            id="metric-name-select"
            :customStyle="{ width: '8rem' }"
            :size="'small'"
          />
        </div>
        <div
          class="chart-rate"
          :class="{
            'increase-rate': metricRate >= 0,
            'decrease-rate': metricRate < 0,
            hidden: !(item.showmetricRate && showMetricRateforAll),
          }"
        >
          <div v-if="metricRate">
            <BaseIcon
              v-if="metricRate !== 0"
              name="arrowDown"
              height="1.5rem"
              width="1rem"
              :class="{
                'arrow-up': metricRate > 0,
                'arrow-down': metricRate < 0,
              }"
              :transform="metricRate > 0 ? 'rotate(180deg)' : ''"
            />
          </div>
          {{ Math.abs(metricRate) }}%
        </div>
      </div>
      <div class="line-content-error" v-if="item.header.isError && childQuery">
        <span
          class="line-error-percentage"
          v-if="!item.header.hidePercentageParam"
        >
          <span v-if="childSource.length == 0 && childQuery.avg">
            {{ childQuery.avg }}%
          </span>
          <span v-else> {{ getChilds('percentage', childSource) }}% </span>
        </span>
        <span v-if="item.header?.isErrorMetricType" class="line-errors">
          {{ getChilds('total', childSource) }} {{ $t('bm.errors') }}
        </span>
        <span v-else class="line-errors">
          {{
            childQuery.total > 0 ? getLargeNumberByUnits(childQuery.total) : 0
          }}
          {{ $t('bm.errors') }}
        </span>
        <span v-if="item.header?.isErrorMetricType">
          {{ getChilds('pm', childSource) }} {{ $t('apm.errors-per-min') }}
        </span>
        <span v-else>
          {{ childQuery.perMin }} {{ $t('apm.errors-per-min') }}
        </span>
      </div>
      <div
        v-else-if="item.header.displayOnlyUnit && childQuery"
        class="line-content"
      >
        {{ childQuery.avg }} {{ item.unit }} {{ $t('average') }}
        <span
          v-if="showApdexMetric(childSource)"
          class="apdex"
          :class="apdexColorCode"
        >
          Apdex {{ getApdexValue(childSource) }}
        </span>
      </div>
      <div v-else class="line-content">
        <div
          v-if="!item.header.HideCenterMiscContent && childQuery"
          class="line-content"
        >
          <span
            class="line-content-calls"
            v-if="
              item.header?.basicInfoStyling &&
              item.header?.basicInfoStyling.enabled &&
              !item.header.hideCPM
            "
          >
            <span :style="item.header.basicInfoStyling.style">
              {{ getLargeNumberByUnits(childQuery.total) }} {{ $t('bm.calls') }}
            </span>
          </span>
          <span class="line-content-calls" v-else-if="!item.header.hideCPM">
            {{
              childQuery.total > 0 ? getLargeNumberByUnits(childQuery.total) : 0
            }}
            {{ $t('bm.calls') }}
          </span>
          <span
            v-if="
              item.header.basicInfoStyling &&
              item.header.basicInfoStyling.enabled &&
              !item.header.hideCPM
            "
          >
            <span :style="item.header.basicInfoStyling.style">
              {{ childQuery.perMin }} {{ $t('callsPerMin') }}
            </span>
          </span>
          <span v-else-if="!item.header.hideCPM">
            {{ childQuery.perMin }} {{ $t('callsPerMin') }}
          </span>
        </div>
      </div>
      <div
        v-if="item?.compareTrend && getIsCompareOptionsSelected"
        class="chart-rate"
        :class="compareTrendClass"
      >
        <BaseIcon
          v-if="compareCalls"
          name="arrowDown"
          height="1.5rem"
          width="1rem"
          :class="compareArrowClass"
          :transform="compareArrowTransform"
        />
        {{ compareCalls }} {{ item.unit }}
      </div>
      <div class="line-icon">
        <router-link
          v-if="item.header.displayCustomButton"
          :to="{
            name: item.header.customButtonProp.routeName
              ? item.header.customButtonProp.routeName
              : 'trace',
            query: item.header.customButtonProp.routeParams
              ? item.header.customButtonProp.routeParams
              : {
                  type: item.header.customButtonProp.key,
                  entity: item.entityType,
                },
          }"
        >
          <div class="custom-button">
            {{ item.header.customButtonProp.title }}
          </div>
        </router-link>
        <div class="icons-wrapper">
          <span
            class="icon-class"
            @click="handleZoomOut"
            :id="`${replaceSpaceInId(item.header.title)}-zoom-out`"
          >
            <BaseIcon name="zoomOut" width="2.438em" height="2.438em" />
          </span>
          <span
            class="icon-class"
            @click="handleZoomIn"
            :id="`${replaceSpaceInId(item.header.title)}-zoom-in`"
          >
            <BaseIcon name="zoomIn" width="2.438em" height="2.438em" />
          </span>
          <span
            class="icon-class"
            @click="handleReset"
            :id="`${replaceSpaceInId(item.header.title)}-zoom-reset`"
          >
            <BaseIcon name="reset" width="1.438em" height="1.438em" />
          </span>
          <RexMoreOptionAction
            :options="moreOptionsData"
            :data="getMoreOptionsData"
            v-if="showMoreOptions"
          />
        </div>
      </div>
    </div>
    <div class="chart-body">
      <apexchart
        height="100%"
        type="line"
        :options="updateOptions"
        :series="series"
        ref="chart"
      ></apexchart>
    </div>
  </div>
</template>
<script>
import BaseIcon from '@/rex-components/BaseIcon.vue';
import shortenLargeNumber from '@/utils/numberShortner';
import {
  ToolTip,
  Legend,
  Markers,
  ChartToolBar,
  Animations,
  XAxisConfig,
  YAxisConfig,
  Grid,
  dashLinesize,
  lineWidth,
  tooltipValues,
  getCustomTooltipContent,
  getSeriesTooltipContent,
} from '@/components/charts/charts.global.config';
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import {
  CAPABILITY_APM,
  CAPABILITY_BROWSER,
  CAPABILITY_MOBILE,
  CAPABILITY_VM,
  CAPABILITY_K8M,
} from '@/utils/constants';
import { replaceSpaceInId } from '@/utils/helper-function';
import RexButtonV3 from '@/rex-components/RexButtonV3.vue';
import {
  ALL_METRIC_OPTION,
  MetricsType,
} from '@/components/charts/constant.ts';
import RexSelectV3 from '@/rex-components/RexSelectV3.vue';
import RexMoreOptionAction from '@/rex-components/RexMoreOptionAction.vue';

export default {
  components: { BaseIcon, RexSelectV3, RexButtonV3, RexMoreOptionAction },
  name: 'ApexChartLine',
  props: {
    data: {},
    type: {},
    intervalTime: {},
    item: {},
    index: {},
    childSource: {},
    childQuery: {},
    compareChildQuery: {
      type: Object,
      default: () => ({}),
    },
    isMultiLineSeriesTooltip: {
      type: Boolean,
      default: false,
    },
    customButtonVisible: Boolean,
    customButtonTextIfVisiable: String,
    moreOptionsData: { type: Array, default: () => [] },
    showMoreOptions: { type: Boolean, default: false },
    compareIntervalTime: {
      type: Array,
      default: () => [],
    },
  },
  data: function () {
    return {
      minX: 1,
      maxX: this.intervalTime.length,
      apdexColorCode: '',
      options: {
        chart: {
          id: `vuechart-line-${this.index}`,
          type: 'line',
          ...this?.item?.isSynchronized,
          animations: Animations,
          background: 'inherit',
          events: {
            zoomed: (chartContext, { xaxis, yaxis }) => {
              this.updateMinMax(xaxis);
            },
            beforeMount: (chartContext, config) => {
              // console.log('config', config);
              //  this.setConfig(config);
            },
          },
          toolbar: ChartToolBar,
        },
        tooltip:
          this.item.metricName.includes(',') ||
          this.item.metricType === MetricsType.LABELED_VALUE
            ? {
                enabled: true,
                enabledOnSeries: true,
                shared: true,
                followCursor: false,
                intersect: false,
                inverseOrder: false,
                custom: undefined,
                fillSeriesColor: false,
                theme: 'dark',
                style: {
                  fontSize: '12px',
                  fontFamily: undefined,
                },
                onDatasetHover: {
                  highlightDataSeries: false,
                },
                x: {
                  show: true,
                  format: 'dd MMM',
                  formatter: undefined,
                },
                y: {
                  formatter: undefined,
                  title: {
                    formatter: (seriesName) => seriesName,
                  },
                },
                z: {
                  formatter: undefined,
                  title: 'Size: ',
                },
                marker: {
                  show: true,
                },
                items: {
                  display: 'flex',
                },
                fixed: {
                  enabled: false,
                  position: 'topRight',
                  offsetX: 0,
                  offsetY: 0,
                },
              }
            : ToolTip,
        legend: !this.item.showLegendForSingle
          ? Legend
          : { ...Legend, showForSingleSeries: true },
        colors: this.item.chartColor,
        grid: Grid,
        xaxis: {
          tickAmount: this.intervalTime.length > 14 ? 5 : 3,
          ...XAxisConfig,
          title: {
            text: this.item.axisLabels.x,
            ...XAxisConfig.title,
          },
          min: this.intervalTime[0],
        },
        yaxis:
          this.item.header.isError &&
          this.childSource &&
          this.item.isPercentageValue
            ? {
                ...YAxisConfig,
                min: 0,
                max: 100,
                tickAmount: 4,
                title: {
                  text: this.item.axisLabels.y,
                  align: 'left',
                  ...YAxisConfig.title,
                  offsetX: 0,
                  offsetY: 0,
                },
              }
            : {
                ...YAxisConfig,
                decimalsInFloat: 3,
                title: {
                  text: this.item.axisLabels.y,
                  ...YAxisConfig.title,
                },
              },
        markers: Markers,
      },
      currentMetric: '',
      metricRate: 0,
      loading: false,
      showMetricRateforAll: true,
    };
  },
  methods: {
    ...mapActions('databaseState/databaseCommonState', [
      'getDatabaseGraphData',
    ]),
    ...mapActions('homeState', ['GET_METRIC_RATE']),
    resize() {
      const chart = this.$refs.chart;
      chart.myChart.resize();
    },
    setConfig(config) {
      // console.log('configss', config.globals.maxX);
      // this.minX = config.globals.maxX;
      // this.maxX = config.globals.maxX;
    },
    handleZoomIn() {
      // Find minMax for x
      try {
        if (this.$refs.chart.chart.w.globals.minX)
          this.minX = this.$refs.chart.chart.w.globals.minX;
        if (this.$refs.chart.chart.w.globals.maxX)
          this.maxX = this.$refs.chart.chart.w.globals.maxX;
      } catch (error) {
        // Todo: Handle error
      }

      if (
        typeof this.minX === 'undefined' &&
        typeof this.maxX === 'undefined'
      ) {
        this.handleReset();
      }
      if (this.maxX - this.minX > 2) {
        const centerX = (this.minX + this.maxX) / 2;
        let newMinX = (this.minX + centerX) / 2;
        let newMaxX = (this.maxX + centerX) / 2;
        const newMinXMaxX = this._getNewMinXMaxX(newMinX, newMaxX);
        this.minX = newMinXMaxX.minX;
        this.maxX = newMinXMaxX.maxX;
        this.$refs.chart.zoomX(this.minX, this.maxX);
      }
    },
    handleReset() {
      this.$refs.chart.zoomX();
      this.minX = 1;
      this.maxX = this.intervalTime.length;
    },
    _getNewMinXMaxX(newMinX, newMaxX) {
      return {
        minX: Math.floor(newMinX),
        maxX: Math.floor(newMaxX),
      };
    },
    updateMinMax(xaxis) {
      this.minX = xaxis.min;
      this.maxX = xaxis.max;
    },
    handleZoomOut() {
      //  Find minMax for x
      //  This checks if min & max are undefined and resets them

      try {
        if (this.$refs.chart.chart.w.globals.minX)
          this.minX = this.$refs.chart.chart.w.globals.minX;
        if (this.$refs.chart.chart.w.globals.maxX)
          this.maxX = this.$refs.chart.chart.w.globals.maxX;
      } catch (error) {
        // Todo: Handle error
      }

      if (
        typeof this.minX === 'undefined' &&
        typeof this.maxX === 'undefined'
      ) {
        this.handleReset();
      }
      if (this.minX > 0 && this.maxX - this.minX >= 1) {
        const centerX = (this.minX + this.maxX) / 2;
        let newMinX = this.minX - (centerX - this.minX);
        let newMaxX = this.maxX - (centerX - this.maxX);
        const newMinXMaxX = this._getNewMinXMaxX(newMinX, newMaxX);
        this.minX = newMinXMaxX.minX;
        this.maxX = newMinXMaxX.maxX;
        this.$refs.chart.zoomX(this.minX, this.maxX);
      }
    },
    getChilds(type, val) {
      if (type === 'pm') {
        const item = val.find((o) => o.metricType === 'pm');
        if (item) {
          return shortenLargeNumber(item.avgNum, 1);
        } else return 0;
      } else if (type === 'percentage') {
        const item = val.find((o) => o.metricType === 'percentage');
        if (item) {
          return item.avgNum ? item.avgNum / 100 : 0;
        } else return 0;
      } else if (type === 'total') {
        const item = val.find((o) => o.metricType === 'total');
        if (item) {
          if (
            item.name === 'service_resp_time' ||
            item.name === 'endpoint_avg' ||
            item.name === 'service_instance_resp_time' ||
            item.name === 'database_access_resp_time'
          ) {
            return item.avgNum;
          } else return shortenLargeNumber(item.avgNum, 1);
        } else return 0;
      } else return 0;
    },
    getLargeNumberByUnits(val) {
      return shortenLargeNumber(val, 1);
    },
    showApdexMetric(val) {
      /**
       * Special Case
       * Modified by @Tarun Rawat
       * Reason: Don't show Apdex score when Synthetic Monitoring is enabled in Browser Mon.
       */
      if (
        this.syntheticUserEnabled &&
        this.currentCapability === CAPABILITY_BROWSER
      )
        return false;

      const item = val.find((o) => o.metricType === 'apdex');
      if (item) {
        this.apdexColorCode = (() => {
          const val = item.avgNum ? item.avgNum : 0;
          if (val >= 8400) {
            return 'good';
          } else if (val >= 6800 && val < 8400) {
            return 'fair';
          } else {
            return 'poor';
          }
        })();
        return true;
      } else return false;
    },
    getApdexValue(val) {
      const item = val.find((o) => o.metricType === 'apdex');
      if (item) {
        return parseFloat(
          (!isNaN(item.avgNum) ? item.avgNum : 0) / 10000,
        ).toFixed(2);
      } else return 0;
    },
    replaceSpaceInId,
    onMetricChange(value, option) {
      this.currentMetric = value;
      this.setCurrentMetrics(option);
      this.showMetricRateforAll = option?.value !== ALL_METRIC_OPTION.value;
    },
    setCurrentMetrics(metric) {
      this.currentMetric = metric;
      this.$emit('metricChanged', { graphId: this.options.chart.id, metric });
    },
    highlightSeries() {
      if (this.$refs.chart) {
        this.$refs.chart.resetSeries();
        if (this.currentMetric?.value !== ALL_METRIC_OPTION.value) {
          this.formattedMetricNames
            .filter((metric) => metric?.value !== ALL_METRIC_OPTION.value)
            .map((metric) => {
              if (metric.value === this.currentMetric.value) {
                this.$refs.chart.showSeries(metric.value);
              } else this.$refs.chart.hideSeries(metric.value);
            });
        }
      }
    },
    async getMetricRate() {
      this.loading = true;
      try {
        if (
          this.currentDatabaseService.key &&
          this.currentDatabaseInstance.key
        ) {
          const params = {
            condition: {
              name:
                this.currentMetric.labledMetricName || this.currentMetric.value,
              entity: {
                scope: 'ServiceInstance',
                serviceName: this.currentDatabaseService.key,
                serviceInstanceName: this.currentDatabaseInstance.key,
                normal: true,
              },
            },
            ...(this.currentMetric.labledMetricName
              ? {
                  keyword: this.currentMetric.value,
                }
              : {
                  keyword: '',
                }),
            duration: this.getDurationTime,
          };
          this.metricRate = parseFloat(await this.GET_METRIC_RATE(params));
        } else this.metricRate = 0;
      } catch (error) {
        console.log(error);
        this.metricRate = 0;
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    ...mapState('browserState/browserCommonState', ['syntheticUserEnabled']),
    ...mapState('globalState/app', [
      'currentCapability',
      'compareSupportedCapabilities',
      'currentSideNav',
    ]),
    ...mapGetters('globalState/settings', [
      'getDurationTime',
      'getIsCompareOptionsSelected',
      'getCompareDurationTime',
    ]),
    ...mapState('databaseState/databaseCommonState', [
      'currentDatabaseService',
    ]),
    ...mapState('databaseState/detail', ['currentDatabaseInstance']),
    labels() {
      return Object.keys(this.data || {}).filter(
        (i) => Array.isArray(this.data[i]) && this.data[i].length,
      );
    },
    getDefaultTooltip() {
      return {
        ...tooltipValues,
      };
    },
    compareArrowClass() {
      return {
        'arrow-up': this.childQuery?.avg > this.compareChildQuery?.avg,
        'arrow-down': this.childQuery?.avg < this.compareChildQuery?.avg,
      };
    },
    compareTrendClass() {
      return {
        'increase-rate': this.childQuery?.avg > this.compareChildQuery?.avg,
        'decrease-rate': this.childQuery?.avg < this.compareChildQuery?.avg,
      };
    },
    compareArrowTransform() {
      return this.childQuery?.avg > this.compareChildQuery?.avg
        ? 'rotate(180deg)'
        : '';
    },
    getCustomTooltip() {
      if (
        !this.compareSupportedCapabilities.includes(this.currentSideNav.key)
      ) {
        return this.options.tooltip;
      } else {
        return {
          ...tooltipValues,
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            return getCustomTooltipContent(
              series,
              this.data,
              this.intervalTime,
              this.compareIntervalTime,
              this.item,
              dataPointIndex,
              w,
            );
          },
        };
      }
    },
    compareCalls() {
      const childAvg = this.childQuery?.avg ?? 0;
      const compareChildAvg = this.compareChildQuery?.avg ?? 0;
      const avgdif = Math.abs(childAvg - compareChildAvg);
      return this.getLargeNumberByUnits(avgdif);
    },
    series() {
      const keys = Object.keys(this.data || {}).filter((i) =>
        Array.isArray(this.data[i]),
      );
      const nonComparedSeries = keys.filter((key) => !key.includes('compared'));
      const comparedSeries = keys.filter((key) => key.includes('compared'));
      const legendName = (isCompared, isCompareOptionsSelected, itemName) => {
        const compareItemName = itemName.replace(/_compared$/, '');
        const isSpecialCapability =
          this.currentCapability === CAPABILITY_BROWSER ||
          this.currentCapability === CAPABILITY_APM ||
          this.currentCapability === CAPABILITY_MOBILE ||
          this.currentCapability === CAPABILITY_VM ||
          this.currentCapability === CAPABILITY_K8M;
        if (isCompared) {
          if (isCompareOptionsSelected && isSpecialCapability) {
            return `${this.$t('previousPeriod')} - ${compareItemName}`;
          }
          return `${this.$t('currentPeriod')} - ${compareItemName}`;
        }
        if (isCompareOptionsSelected && isSpecialCapability) {
          return `${this.$t('currentPeriod')} - ${compareItemName}`;
        }
        return compareItemName;
      };
      const sortedKeys = [...nonComparedSeries, ...comparedSeries];
      const series = sortedKeys.map((i) => {
        const isCompared = i.includes('compared');
        return {
          name: legendName(isCompared, this.getIsCompareOptionsSelected, i),
          data: this.intervalTime.map((x) => {
            const itemIndex = this.intervalTime.indexOf(x);
            return {
              x: x,
              y:
                this.item.header.isError &&
                this.childSource &&
                this.item.isPercentageValue
                  ? this.data[i]?.[itemIndex] && this.data[i][itemIndex] / 100
                  : itemIndex !== -1
                    ? this.data[i][itemIndex]
                    : null,
            };
          }),
        };
      });
      return series;
    },
    updateOptions() {
      const keys = Object.keys(this.data || {}).filter(
        (i) => Array.isArray(this.data[i]) && this.data[i].length,
      );
      const getErrorPercentage = (key) => {
        let item;
        item = this.childSource?.find((o) => o.name === key);
        if (item)
          return `<span style="color:#ef6654;">${
            item.avgNum ? item.avgNum / 100 : 0
          }</span>`;
        else return `<span style="color:#ef6654;">0</span>`;
      };
      const nonComparedSeries = keys.filter((key) => !key.includes('compared'));
      const comparedSeries = keys.filter((key) => key.includes('compared'));
      const sortedKeys = [...nonComparedSeries, ...comparedSeries];
      const dashArray = [];
      const widthArray = [];
      sortedKeys.forEach((i) => {
        const isCompared = i.includes('compared');
        dashArray.push(isCompared ? dashLinesize : 0);
        widthArray.push(lineWidth);
      });

      const getShortName = (seriesName) => {
        if (seriesName === 'service_error_code__4xx_percent') return '4XX';
        if (seriesName === 'service_error_code__5xx_percent') return '5XX';
        if (
          seriesName ===
          `${this.$t('currentPeriod')} - service_error_code__4xx_percent`
        )
          return `${this.$t('currentPeriod')} - 4XX`;
        if (
          seriesName ===
          `${this.$t('currentPeriod')} - service_error_code__5xx_percent`
        )
          return `${this.$t('currentPeriod')} - 5XX`;
        if (
          seriesName ===
          `${this.$t('previousPeriod')} - service_error_code__4xx_percent`
        )
          return `${this.$t('previousPeriod')} - 4XX`;
        if (
          seriesName ===
          `${this.$t('previousPeriod')} - service_error_code__5xx_percent`
        )
          return `${this.$t('previousPeriod')} - 5XX`;
        return seriesName;
      };
      const customLegendFormatter = (seriesName, opts) => {
        const seriesIndex = opts.seriesIndex;
        const colors = this.updateOptions.colors || [];
        const colorIndex = seriesIndex % nonComparedSeries.length;
        const seriesColor = colors[colorIndex] || '#5d5e5f';
        const isPreviousPeriod = seriesName.includes(
          `${this.$t('previousPeriod')}`,
        );
        const circleStyle = isPreviousPeriod
          ? `border-color: ${seriesColor}; border-style: dashed;`
          : `background-color: ${seriesColor};`;

        const circleClass = isPreviousPeriod
          ? 'legend-dashed-circle'
          : 'legend-circle';

        if (
          this.item.header.isError &&
          this.childSource &&
          this.item.isPercentageValue
        ) {
          return [
            `<span class="${circleClass}" style="${circleStyle}"></span> `,
            getShortName(seriesName),
            ': ',
            getErrorPercentage(sortedKeys[seriesIndex]),
            `<span style="color:#ef6654;">%</span>`,
          ].join('');
        }
        return `<span class="${circleClass}" style="${circleStyle}"></span> ${seriesName}`;
      };

      const newLegendOptions = {
        ...this.options.legend,
        show: true,
        showForNullSeries: true,
        markers: {
          width: 0,
          height: 0,
        },
        formatter: customLegendFormatter,
      };
      const getRepeatedColors = (count) => {
        if (!this.item?.chartColor || this.item.chartColor.length === 0) {
          return [];
        }
        return Array.from(
          { length: count },
          (_, i) => this.item.chartColor[i % this.item.chartColor.length],
        );
      };
      const nonComparedColors = getRepeatedColors(nonComparedSeries.length);
      const comparedColors = getRepeatedColors(comparedSeries.length);
      const finalChartColors = [...nonComparedColors, ...comparedColors];
      const updatedTemp = {
        ...this.options,
        colors:
          this.getIsCompareOptionsSelected &&
          (this.currentCapability === CAPABILITY_BROWSER ||
            this.currentCapability === CAPABILITY_APM ||
            this.currentCapability === CAPABILITY_MOBILE ||
            this.currentCapability === CAPABILITY_VM ||
            this.currentCapability === CAPABILITY_K8M)
            ? finalChartColors
            : Array.isArray(this.item.chartColor)
              ? [...this.item.chartColor]
              : [],
        stroke: {
          dashArray,
          width: widthArray,
        },
        legend: newLegendOptions,
        tooltip:
          this.getIsCompareOptionsSelected &&
          (this.currentCapability === CAPABILITY_BROWSER ||
            this.currentCapability === CAPABILITY_APM ||
            this.currentCapability === CAPABILITY_MOBILE ||
            this.currentCapability === CAPABILITY_VM ||
            this.currentCapability === CAPABILITY_K8M)
            ? this.getCustomTooltip
            : this.options.tooltip,
      };
      return updatedTemp;
    },
    formattedMetricNames() {
      if (this.item.metricType === MetricsType.LABELED_VALUE) {
        return [
          ALL_METRIC_OPTION,
          ...this.labels.filter(Boolean).map((label) => {
            return {
              value: label,
              label: label,
              labledMetricName: this.item.metricName,
            };
          }),
        ];
      }
      return [
        ALL_METRIC_OPTION,
        ...this.item.metricName.split(',').map((metric) => {
          const trimmedMetric = metric.trim();
          return { value: trimmedMetric, label: trimmedMetric };
        }),
      ];
    },
    getErrorPercentage() {
      if (this.childSource.totalValueAlt.total == 0) {
        return 0;
      }
      var error_percent =
        (this.childSource.totalValue.total /
          this.childSource.totalValueAlt.total) *
        100;
      if (isNaN(error_percent)) {
        return 0;
      }
      return error_percent.toFixed(2);
    },
    getMoreOptionsData() {
      return {
        series: this.series,
        title: this.item.header.title,
      };
    },
  },
  created() {
    if (this.item.showMetricDropdown) {
      this.currentMetric = ALL_METRIC_OPTION;
      this.showMetricRateforAll = false;
      this.currentMetric = this.formattedMetricNames[0];
    } else {
      this.currentMetric = this.formattedMetricNames[1];
      this.getMetricRate();
    }
  },

  watch: {
    minX(oldVal, newVal) {
      // console.log('oldval', oldVal);
      // console.log('newval', newVal);
    },
    currentMetric() {
      this.getMetricRate();
      this.highlightSeries();
    },
    item() {
      this.currentMetric = this.formattedMetricNames[0];
    },
    labels(newLabels) {
      if (!newLabels.filter(Boolean).length) {
        this.currentMetric = this.formattedMetricNames[0];
      }

      if (!this.currentMetric) {
        this.currentMetric = this.formattedMetricNames[0];
      }
    },
    getDurationTime() {
      if (this.showMetricRateforAll) {
        this.getMetricRate();
      }
    },
    currentDatabaseInstance() {
      if (this.showMetricRateforAll) {
        this.getMetricRate();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.chart-body {
  margin-right: 2px;
  margin-left: 0.125em;
  height: 20.625em;
  border-radius: 1.0625em;
}

.chart-line {
  width: 100%;
  border-radius: 1.0625em;
  background-color: inherit;
  color: $font-primary !important;
}
.line-content-error {
  color: $poor;
  margin-left: 1em;
  .line-error-percentage {
    margin-right: 1.125rem;
  }
  .line-errors {
    margin-right: 1em;
  }
}
.line-content {
  margin-left: 1em;
  .line-content-calls {
    margin-right: 1.3em;
  }
}
.line-header {
  height: 2.375em;
  border-bottom: 0.0063em solid $border-primary;
  @include flex-center-justify(space-between);
  color: $font-primary;

  .line-title {
    margin-left: 0.8625em;

    font-family: $semi-bold;
  }
  .line-icon {
    margin-right: 0.8125em;
    @include flex-center-justify(space-between);
  }

  .icon-margin {
    margin-right: 0.5em;
  }

  .icon-class:hover {
    cursor: pointer;
    svg {
      fill: $font-secondary !important;
    }
  }
  .icon-class:last-child {
    margin-left: 0.3125em;
  }
  .icon-class:first-child {
    margin-right: -0.3125em;
  }
}

.custom-button {
  // margin: 0rem 1.0625em;
  background: $color-brand-accent;
  width: 6.94em;
  height: 1.875em;
  color: $font-contrast;
  // height: 1.92em;
  border-radius: 0.4375em;
  // line-height: 2.1875em;
  cursor: pointer;
  // font-family: RakutenSansUI-Regular;
  font-size: 0.75em;
  text-align: center;
  align-items: center;
  // line-height: 1.25em;
  padding-top: 2px;
  &:hover {
    text-decoration: underline;
  }
}

.apdex {
  padding-left: 20px;
}
.apdex.poor {
  color: $poor;
}
.apdex.fair {
  color: $fair;
}
.apdex.good {
  color: $good;
}
.chart-rate {
  @include flex-center-vert;
  border-radius: 0.2rem;
  padding: 0 0.2rem 0 0.2rem;
}
.chart-rate.rate-positive {
  color: $good;
  background-color: rgba(0, 128, 0, 0.5);
}

.chart-rate.rate-negative {
  color: $poor;
}

.container {
  display: flex;
  gap: 0.8rem;
  width: 5rem;
  margin-right: -1.8rem;
  justify-content: flex-end;
  align-items: center;
}
.btn- {
  @include btn-fill($font-primary);
  padding: none;
  margin: 0px 0.275rem;
}
.increase-rate {
  color: $good;
  background-color: rgba(0, 128, 0, 0.5);
}

.decrease-rate {
  background-color: rgb(47, 38, 38);
  color: $poor;
}
.arrow-down {
  margin-left: 0.2rem;
  fill: $poor;
  margin-right: 0.2rem;
}
.arrow-up {
  margin-left: 0.2rem;
  fill: $good;
  margin-right: 0.2rem;
}
.icon-margin-suffix {
  margin-right: 0.5em;
  @include svg-fill($color-active-blue);
  vertical-align: super;
}
.icons-wrapper {
  @include flex-center;
}
:deep(.legend-circle) {
  display: inline-block;
  height: 12px;
  width: 12px;
  border-width: 0px;
  border-radius: 50%;
}
:deep(.legend-dashed-circle) {
  display: inline-block;
  height: 12px;
  width: 12px;
  border-radius: 50%;
  border: 2px dashed currentColor;
  background-color: transparent;
}
:deep(.tooltip-delta) {
  margin-left: 0.5rem;
}
:deep(.custom-title) {
  font-size: 12px;
  background: black;
  padding: 0.6rem;
}
:deep(.tooltip-text) {
  font-size: 12px;
  text-align: left;
  padding: 4px;
}
.tooltip-section {
  text-align: left;
  margin-bottom: 10px;
}
:deep(.tooltip-series-group) {
  display: flex;
  align-items: center;
}
:deep(.tooltip-marker) {
  margin-right: 5px;
  display: inline-block;
  height: 10px;
  width: 10px;
  border-width: 0px;
  border-radius: 50%;
}
:deep(.series-tooltip-font-group) {
  font-size: 12px;
}
:deep(.series-tooltip-group) {
  font-size: 12px;
  margin-left: 4px;
}
</style>
